import { Box, Button, useDisclosure } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CreateUpdateQuotationItemInputWRowId,
  InputCartTableProps,
} from "./interface";
import TableListTitle from "../TableList/TableListTitle";
import CardFormAlt from "../CardFormAlt";
import update from "immutability-helper";
import { useConfirmation } from "../../contexts/ConfirmationContext";
import { createColumnHelper } from "@tanstack/react-table";
import ModalFormQuotationItem from "../ModalFormQuotationItem";
import DefaultTable from "../DefaultTable/DefaultTable";
import { v4 as uuidv4 } from "uuid";
import NumberFormat from "../NumberFormat";

const columnHelper = createColumnHelper<CreateUpdateQuotationItemInputWRowId>();

const InputQuotationItemTable: React.FC<InputCartTableProps> = ({
  value,
  onChange,
}) => {
  const [items, setItems] = useState<CreateUpdateQuotationItemInputWRowId[]>(
    []
  );
  const [selectedItem, setSelectedItem] =
    useState<CreateUpdateQuotationItemInputWRowId | null>(null);

  useEffect(() => {
    setItems(value?.map((elm) => ({ ...elm, rowId: uuidv4() })) || []);
  }, [value]);

  const addItem = (data: CreateUpdateQuotationItemInputWRowId) => {
    const newItems = update(items, {
      $push: [data],
    });
    setItems(newItems);
    onChange(newItems?.map(({ rowId, ...elm }) => elm));
  };

  const editItem = useCallback(
    (index: number, data: CreateUpdateQuotationItemInputWRowId) => {
      const newItems = update(items, {
        [index]: {
          $set: data,
        },
      });
      setItems(newItems);
      onChange(newItems?.map(({ rowId, ...elm }) => elm));
    },
    [items, onChange]
  );

  const { confirmation, close } = useConfirmation();

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("numberOfPax", {
        id: "numberOfPax",
        header: () => "No. Of Pax",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("pricePerPax", {
        id: "pricePerPax",
        header: () => "Price per Pax",
        cell: ({ getValue }) => <NumberFormat value={getValue()} prefix="$ " />,
      }),
      columnHelper.accessor("airline", {
        id: "airline",
        header: () => "Airline",
      }),
      columnHelper.accessor("class", {
        id: "class",
        header: () => "Class",
      }),
    ];
  }, [close, confirmation, items, onChange]);

  const { isOpen, onClose, onOpen } = useDisclosure();

  const deleteRow = (row: CreateUpdateQuotationItemInputWRowId) => {
    const index = items.findIndex((elm) => elm.id === row.id);

    confirmation({
      description: "Are you sure want to delete?",
      callback: async () => {
        const newItems = update(items, {
          $splice: [[index, 1]],
        });
        setItems(newItems);
        onChange(newItems?.map(({ rowId, ...elm }) => elm));
        close();
      },
    });
  };

  return (
    <>
      <CardFormAlt>
        <TableListTitle
          title="Item"
          actionComponents={
            <Button size="sm" onClick={onOpen}>
              Add Item
            </Button>
          }
        />
        <DefaultTable
          hideTopDivider
          data={items}
          columns={columns}
          getRowId={(row) => row.id as string}
          hasMenu
          onDelete={(row) => deleteRow(row)}
          onEdit={(row) => {
            setSelectedItem(row);
            onOpen();
          }}
        />
        {items.length <= 0 && (
          <Box textAlign="center" mt={2}>
            Items empty
          </Box>
        )}
      </CardFormAlt>
      <ModalFormQuotationItem
        value={selectedItem}
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setSelectedItem(null);
        }}
        onSubmit={(data, callback) => {
          if (selectedItem) {
            editItem(
              items.findIndex((elm) => elm.rowId === selectedItem.rowId),
              { ...selectedItem, ...data }
            );
          } else {
            addItem({ rowId: uuidv4(), ...data });
          }
          callback();
          onClose();
        }}
      />
    </>
  );
};

export default InputQuotationItemTable;
